import { makeCygnusApiCall } from "../../api/api";
import { SavedGridFilters, buildFilterParamName, safeJsonParse, GridExportPayload, ExportFormat } from "./CygnusGrid.utils";

/** GET saved filters via personalization.get_params */
export async function fetchGridFilters(
  userId: string,
  gridKey: string
): Promise<SavedGridFilters | null> {
  const firm_id = localStorage.getItem("firmId");
  if (!firm_id) return null;

  const parameter_name = buildFilterParamName(gridKey, userId);

  const res = await makeCygnusApiCall({
    type: "POST",
    url: `/personalization/get_params/`,
    payload: { firm_id, parameter_name },
    show_message: false,
    customized_success_message: "",
    customized_failure_message: "Unable to fetch saved filters.",
    customized_error_message: "Error while fetching saved filters.",
    isblob: false,
    forMDM: false,
  });

  if (!res?.success) return null;

  const raw = res?.data?.[0]?.params?.[0]?.parameter_value_1 ?? null;
  return safeJsonParse<SavedGridFilters>(raw);
}

/** SET saved filters via personalization.set_params (flat payload) */
export async function saveGridFilters(payload: SavedGridFilters): Promise<boolean> {
  const firm_id = localStorage.getItem("firmId");
  if (!firm_id) return false;

  const parameter_name = buildFilterParamName(payload.gridKey, payload.userId);
  const parameter_value_1 = JSON.stringify(payload);

  const res = await makeCygnusApiCall({
    type: "POST",
    url: `/personalization/set_params/`,
    payload: { firm_id, parameter_name, parameter_value_1 },
    show_message: false,
    customized_success_message: "Filters saved.",
    customized_failure_message: "Unable to save filters.",
    customized_error_message: "Error while saving filters.",
    isblob: false,
    forMDM: false,
  });

  return !!res?.success;
}

/** CLEAR saved filters (set empty string) */
export async function clearGridFilters(userId: string, gridKey: string): Promise<boolean> {
  const firm_id = localStorage.getItem("firmId");
  if (!firm_id) return false;

  const parameter_name = buildFilterParamName(gridKey, userId);

  const res = await makeCygnusApiCall({
    type: "POST",
    url: `/personalization/set_params/`,
    payload: { firm_id, parameter_name, parameter_value_1: "" },
    show_message: false,
    customized_success_message: "Filters cleared.",
    customized_failure_message: "Unable to clear filters.",
    customized_error_message: "Error while clearing filters.",
    isblob: false,
    forMDM: false,
  });

  return !!res?.success;
}

// ============================================
// GRID EXPORT API
// ============================================

/**
 * Export full grid data via backend
 * Returns a Blob for download
 */
export async function exportGridData(payload: GridExportPayload): Promise<Blob | null> {
  const firm_id = localStorage.getItem("firmId");
  if (!firm_id) {
    console.error("exportGridData: No firmId in localStorage");
    return null;
  }

  try {
    const res = await makeCygnusApiCall({
      type: "POST",
      url: `/grid/export/`,
      payload: { ...payload, firm_id },
      show_message: false,
      customized_success_message: "",
      customized_failure_message: "Unable to export data.",
      customized_error_message: "Error during export.",
      isblob: true,
      forMDM: false,
    });

    // Handle different response structures for blob requests
    // Option 1: Response is the Blob directly
    if (res instanceof Blob) {
      // Validate it's not an error response disguised as blob
      if (res.size > 0) {
        return res;
      }
      console.error("exportGridData: Empty blob received");
      return null;
    }

    // Option 2: Response is wrapped in {success, data}
    if (res?.success && res?.data instanceof Blob) {
      if (res.data.size > 0) {
        return res.data;
      }
      console.error("exportGridData: Empty blob in response data");
      return null;
    }

    // Option 3: Response has data property that is a Blob (without success flag)
    if (res?.data instanceof Blob) {
      if (res.data.size > 0) {
        return res.data;
      }
      console.error("exportGridData: Empty blob in response data");
      return null;
    }

    // If we got here, response structure is unexpected
    console.error("exportGridData: Unexpected response structure", res);
    
    // Last resort: check if res has any blob-like properties
    if (res && typeof res === 'object') {
      // Log the structure to help debug
      console.log("Response keys:", Object.keys(res));
      console.log("Response type:", typeof res);
      if (res.size !== undefined && res.type !== undefined) {
        // It might be a Blob-like object
        return res as Blob;
      }
    }

    return null;
  } catch (error) {
    console.error("exportGridData: Exception", error);
    throw error;
  }
}

/**
 * Trigger browser download from a Blob
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Generate a timestamped filename for exports
 */
export function generateExportFilename(
  gridKey: string,
  format: ExportFormat
): string {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, "");
  const ext = format === "excel" ? "xlsx" : "csv";
  return `${gridKey}_${timestamp}.${ext}`;
}